import { useEffect, useState } from 'react';
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { Pravo } from '@helfr/domain';
import { APP_NAME } from '@helfr/domain';
import {
  AlertTriangle,
  ArrowRightLeft,
  Bell,
  BookOpen,
  Camera,
  Clock4,
  CheckCircle2,
  Coins,
  FileSignature,
  HardHat,
  KeyRound,
  LayoutDashboard,
  Lightbulb,
  LogOut,
  Megaphone,
  Menu as MenuIkona,
  Ruler,
  Search,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Link } from 'react-router';
import { api } from '../api/client.js';
import type {
  DenikZaznam,
  Firma,
  HlidaceSouhrnZakazky,
  NalezyOdpoved,
  Zakazka,
} from '../api/types.js';
import { useAuth } from '../auth/AuthContext.js';
import { useVybranaZakazka } from '../lib/globalniZakazka.js';
import { Menu, Nacitani, Select } from './ui.js';
import { ZmenaHeslaDialog } from './ZmenaHesla.js';

/**
 * Levá navigace kanceláře (UX audit B1.3–B1.4 / TOP 6): seskupená do bloků
 * jazykem stavby — STAVBA (co se na stavbě děje), PENÍZE (cesta peněz),
 * NASTAVENÍ. Položka se ukáže jen s příslušným právem (API by jinak vrátilo
 * 403). Názvy sedí s obsahem: „Nálezy", „Vícepráce", „Soupis prací (import)".
 */
interface NavPolozka {
  cesta: string;
  nazev: string;
  Ikona: LucideIcon;
  pravo: Pravo;
}
/** Očíslované kroky jednoho procesu — v menu vykreslené jako spojený blok. */
interface NavProces {
  nazev: string;
  kroky: (NavPolozka & { krok: number })[];
}
interface NavSkupina {
  nadpis: string;
  proces?: NavProces;
  polozky: NavPolozka[];
}

// Jednotný ikonový systém (lucide, line ikony) — stejný koncept, stejný symbol
// napříč appkou vč. terénní lišty (Recognition/Jakob).
const NAV_SKUPINY: NavSkupina[] = [
  {
    nadpis: 'STAVBA',
    // „Přehled" tu NENÍ — je to nadřazený rozcestník nad skupinami (vykreslen
    // samostatně nad STAVBA). „Zakázky" tu záměrně NEJSOU — zakázka se řeší
    // v přepínači kontextu nahoře (+ odkaz „Všechny zakázky"), ať není dvakrát.
    polozky: [
      { cesta: '/denik', nazev: 'Deník', Ikona: BookOpen, pravo: 'projekty.read' },
      { cesta: '/vykresy', nazev: 'Výkresy', Ikona: Ruler, pravo: 'projekty.read' },
      { cesta: '/vyzvy', nazev: 'Výzvy', Ikona: Megaphone, pravo: 'projekty.read' },
      { cesta: '/fotogalerie', nazev: 'Fotky', Ikona: Camera, pravo: 'projekty.read' },
      { cesta: '/dochazka', nazev: 'Docházka', Ikona: Clock4, pravo: 'projekty.read' },
    ],
  },
  {
    nadpis: 'PENÍZE',
    // Nálezy → Změny → Rekapitulace nejsou tři agendy, ale TŘI FÁZE jednoho
    // procesu (hlídač víceprací) — v menu vykreslené jako spojený očíslovaný
    // blok, ať je posloupnost vidět na první pohled.
    proces: {
      nazev: 'Vícepráce',
      kroky: [
        {
          krok: 1,
          cesta: '/nalezy',
          nazev: 'Nálezy',
          Ikona: AlertTriangle,
          pravo: 'zmeny.kvalifikovat',
        },
        { krok: 2, cesta: '/zmeny', nazev: 'Změny', Ikona: ArrowRightLeft, pravo: 'finance.read' },
        {
          krok: 3,
          cesta: '/rekapitulace',
          nazev: 'Rekapitulace',
          Ikona: Coins,
          pravo: 'finance.read',
        },
      ],
    },
    polozky: [
      // Smlouvy = základ cesty peněz (SOD → ceník → ZL → dodatek), ne nastavení.
      { cesta: '/smlouvy', nazev: 'Smlouvy', Ikona: FileSignature, pravo: 'smlouvy.read' },
      // „Dodatky" ani „Soupis prací (import)" v menu záměrně NEJSOU — patří ke
      // smlouvě: dodatek se sestavuje ze ZL (detail Změny → „Zařadit do
      // dodatku…") nebo ze smlouvy (sekce Dodatky), import ze sekce Ceník.
      // Routy /dodatky a /import žijí dál (deep-linky, registr).
    ],
  },
  {
    nadpis: 'NASTAVENÍ',
    polozky: [{ cesta: '/uzivatele', nazev: 'Uživatelé', Ikona: Users, pravo: 'admin.users' }],
  },
];

/**
 * Kam přesměrovat po přepnutí zakázky: detail (s :id v cestě) patří STARÉ
 * zakázce — zůstat na něm by vypadalo, že přepínač nefunguje. Odskočí se na
 * seznam dané agendy; seznamové stránky zůstávají (samy se přenačtou).
 */
function cilPoPrepnutiZakazky(pathname: string): string | null {
  const [, prvni, druhy, treti] = pathname.split('/');
  if (!prvni || !druhy) return null;
  if (prvni === 'zakazky') return treti === 'rekapitulace' ? '/rekapitulace' : '/zakazky';
  if (prvni === 'protokoly') return '/smlouvy';
  if (prvni === 'smlouvy' && druhy === 'nova') return null;
  return ['zmeny', 'smlouvy', 'dodatky', 'vykresy', 'nalezy'].includes(prvni) ? `/${prvni}` : null;
}

/** Řádek v panelu upozornění: ikona dle závažnosti + text + cíl. */
function UpozorneniRadek({
  ton,
  text,
  to,
}: {
  ton: 'error' | 'warning' | 'info';
  text: string;
  to: string;
}) {
  const Ikona = ton === 'error' ? AlertTriangle : ton === 'warning' ? Lightbulb : Search;
  const chip =
    ton === 'error'
      ? 'bg-error-bg text-error-text'
      : ton === 'warning'
        ? 'bg-warning-bg text-warning-text'
        : 'bg-info-bg text-info-text';
  return (
    <Link
      to={to}
      className="flex items-start gap-2.5 rounded-lg px-2 py-2 transition-colors duration-100 hover:bg-bg"
    >
      <span
        aria-hidden="true"
        className={`mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full ${chip}`}
      >
        <Ikona size={13} strokeWidth={2} />
      </span>
      <span className="line-clamp-3 min-w-0 text-sm text-text-primary">{text}</span>
    </Link>
  );
}

/**
 * Desktopové rozvržení kanceláře (přípravář/admin): seskupená levá navigace
 * dle práv, hlavička s firmou, globálním přepínačem zakázky a uživatelem.
 */
export function Layout() {
  const { me, nacitani, maPravo, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // Mobilní drawer navigace (< lg je sidebar skrytý za hamburgerem).
  const [menuOtevrene, setMenuOtevrene] = useState(false);
  const [menimHeslo, setMenimHeslo] = useState(false);
  useEffect(() => setMenuOtevrene(false), [location.pathname]);

  const { data: firma } = useQuery({
    queryKey: ['firma'],
    queryFn: () => api.get<Firma>('/api/tenants/me'),
    enabled: !!me,
    staleTime: 10 * 60 * 1000,
  });

  // Zakázky pro globální přepínač (B1.2) — jen s právem čtení zakázek.
  const { data: zakazky } = useQuery({
    queryKey: ['zakazky'],
    queryFn: () => api.get<Zakazka[]>('/api/projects'),
    enabled: !!me && maPravo('projekty.read'),
    staleTime: 60 * 1000,
  });
  const { vybranaId, nastav } = useVybranaZakazka(zakazky);

  // Badge počtu nových nálezů ve frontě (B1.4: „Fronta nálezů (2)").
  const { data: novaFronta } = useQuery({
    queryKey: ['nalezy', 'stav=novy'],
    queryFn: () => api.get<NalezyOdpoved>('/api/nalezy?stav=novy'),
    enabled: !!me && maPravo('zmeny.kvalifikovat'),
    staleTime: 60 * 1000,
  });
  const pocetNovychNalezu = novaFronta?.nalezy.length ?? 0;

  // Zdroje upozornění do zvonku (topbar): hlídače VŠECH živých zakázek
  // (zvonek nesmí lhát) + nepodepsané deníky vybrané. Aktuální zakázka první.
  const maFinance = maPravo('finance.read');
  const { data: hlidaceSouhrn } = useQuery({
    queryKey: ['hlidace-souhrn'],
    queryFn: () => api.get<HlidaceSouhrnZakazky[]>('/api/zakazky/hlidace-souhrn'),
    enabled: !!me && maFinance,
    staleTime: 60 * 1000,
  });
  const hlidaceAktualni = (hlidaceSouhrn ?? []).find((z) => z.zakazka.id === vybranaId);
  const hlidaceOstatni = (hlidaceSouhrn ?? []).filter((z) => z.zakazka.id !== vybranaId);
  const { data: dnyDeniku } = useQuery({
    queryKey: ['denik-dny', vybranaId],
    queryFn: () => api.get<DenikZaznam[]>(`/api/diary?zakazkaId=${vybranaId}`),
    enabled: !!me && maPravo('projekty.read') && !!vybranaId,
    staleTime: 60 * 1000,
  });
  const nepodepsane = (dnyDeniku ?? []).filter((d) => d.nepodepsany).length;
  const pocetHlidacu = (hlidaceSouhrn ?? []).reduce(
    (n, z) => n + z.alarmy.length + z.pripominky.length,
    0,
  );
  const pocetUpozorneni = pocetHlidacu + pocetNovychNalezu + nepodepsane;
  const jeAlarm = (hlidaceSouhrn ?? []).some((z) => z.alarmy.length > 0);

  if (nacitani) return <Nacitani text="Ověřuji přihlášení…" />;
  if (!me) return <Navigate to="/login" state={{ od: location.pathname }} replace />;

  /** Iniciály do avataru účtu („Petra Přípravářová" → „PP"). */
  const inicialy = me.jmeno
    .split(/\s+/)
    .map((s) => s[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    // App shell (SaaS): pevný sidebar + pevná horní lišta, scroluje JEN obsah.
    // Na mobilu je sidebar vysouvací drawer přes obsah (hamburger v hlavičce).
    <div className="flex h-screen overflow-hidden bg-bg">
      {menuOtevrene && (
        <div
          aria-hidden="true"
          className="fixed inset-0 z-40 bg-overlay lg:hidden"
          onClick={() => setMenuOtevrene(false)}
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-60 shrink-0 flex-col bg-primary text-white transition-transform duration-200 lg:static lg:z-auto lg:translate-x-0 ${
          menuOtevrene ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Značka: mark (přilba v akcent chipu) + wordmark. */}
        <div className="flex items-center gap-2.5 px-4 pt-5 pb-4">
          <span className="flex size-8 items-center justify-center rounded-lg bg-accent text-text-heading">
            <HardHat size={18} strokeWidth={2} aria-hidden="true" />
          </span>
          <span className="text-lg font-bold tracking-tight">{APP_NAME}</span>
          <button
            type="button"
            aria-label="Zavřít menu"
            className="ml-auto flex size-9 items-center justify-center rounded-lg text-white/70 hover:bg-white/10 hover:text-white lg:hidden"
            onClick={() => setMenuOtevrene(false)}
          >
            <X size={20} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>

        <nav
          aria-label="Hlavní navigace"
          className="flex flex-1 flex-col gap-5 overflow-y-auto px-3 pb-4"
        >
          {/* Přehled = nadřazený rozcestník — samostatně NAD skupinami. */}
          {maPravo('finance.read') && (
            <NavLink
              to="/prehled"
              className={({ isActive }) =>
                `relative flex h-10 items-center gap-3 rounded-lg px-3 text-sm font-medium transition-colors duration-100 ${
                  isActive
                    ? 'bg-white/10 text-white'
                    : 'text-white/70 hover:bg-white/5 hover:text-white'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span
                      aria-hidden="true"
                      className="absolute top-1/2 left-0 h-5 w-0.5 -translate-y-1/2 rounded-full bg-accent"
                    />
                  )}
                  <LayoutDashboard
                    size={18}
                    strokeWidth={1.75}
                    aria-hidden="true"
                    className="shrink-0"
                  />
                  <span className="min-w-0 flex-1 truncate">Přehled</span>
                </>
              )}
            </NavLink>
          )}

          {NAV_SKUPINY.map((skupina) => {
            const viditelne = skupina.polozky.filter((p) => maPravo(p.pravo));
            const kroky = skupina.proces?.kroky.filter((p) => maPravo(p.pravo)) ?? [];
            if (viditelne.length === 0 && kroky.length === 0) return null;
            return (
              <div key={skupina.nadpis} className="flex flex-col gap-0.5">
                <p className="px-3 pb-1 text-[11px] font-semibold tracking-wider text-white/45">
                  {skupina.nadpis}
                </p>

                {viditelne.map((p) => (
                  <NavLink
                    key={p.cesta}
                    to={p.cesta}
                    className={({ isActive }) =>
                      `relative flex h-10 items-center gap-3 rounded-lg px-3 text-sm font-medium transition-colors duration-100 ${
                        isActive
                          ? 'bg-white/10 text-white'
                          : 'text-white/70 hover:bg-white/5 hover:text-white'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <span
                            aria-hidden="true"
                            className="absolute top-1/2 left-0 h-5 w-0.5 -translate-y-1/2 rounded-full bg-accent"
                          />
                        )}
                        <p.Ikona
                          size={18}
                          strokeWidth={1.75}
                          aria-hidden="true"
                          className="shrink-0"
                        />
                        <span className="min-w-0 flex-1 truncate">{p.nazev}</span>
                      </>
                    )}
                  </NavLink>
                ))}

                {/* Proces (Vícepráce): očíslované kroky ve spojeném bloku —
                    posloupnost 1→2→3 je vidět přímo v navigaci. */}
                {skupina.proces && kroky.length > 0 && (
                  <div className="mb-1 rounded-lg bg-white/5 p-1.5">
                    <p className="px-1.5 pb-1 text-[11px] font-semibold tracking-wider text-accent">
                      {skupina.proces.nazev}
                    </p>
                    {kroky.map((p) => (
                      <NavLink
                        key={p.cesta}
                        to={p.cesta}
                        className={({ isActive }) =>
                          `relative flex h-10 items-center gap-2.5 rounded-lg px-2 text-sm font-medium transition-colors duration-100 ${
                            isActive
                              ? 'bg-white/10 text-white'
                              : 'text-white/70 hover:bg-white/5 hover:text-white'
                          }`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <span
                              aria-hidden="true"
                              className={`flex size-5 shrink-0 items-center justify-center rounded text-[11px] font-bold ${
                                isActive ? 'bg-accent text-text-heading' : 'bg-white/10'
                              }`}
                            >
                              {p.krok}
                            </span>
                            <span className="min-w-0 flex-1 truncate">{p.nazev}</span>
                            {p.cesta === '/nalezy' && pocetNovychNalezu > 0 && (
                              <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-accent px-1.5 py-0.5 text-xs font-bold text-text-heading">
                                {pocetNovychNalezu}
                              </span>
                            )}
                          </>
                        )}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Účet dole vlevo: avatar + jméno/role, přepnutí do terénu, odhlášení. */}
        <div className="flex flex-col gap-0.5 border-t border-white/10 px-3 py-3">
          <div className="flex items-center gap-2.5 px-3 py-1.5">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-white/10 text-xs font-bold text-white">
              {inicialy}
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold text-white">{me.jmeno}</span>
              <span className="block truncate text-xs text-white/55">{me.role.join(', ')}</span>
            </span>
          </div>
          {maPravo('denik.write') && (
            <NavLink
              to="/teren/denik"
              className="flex h-10 items-center gap-3 rounded-lg px-3 text-sm font-medium text-white/70 transition-colors duration-100 hover:bg-white/5 hover:text-white"
            >
              <HardHat size={18} strokeWidth={1.75} aria-hidden="true" className="shrink-0" />
              Přepnout do terénu
            </NavLink>
          )}
          <button
            type="button"
            onClick={() => setMenimHeslo(true)}
            className="flex h-10 items-center gap-3 rounded-lg px-3 text-left text-sm font-medium text-white/70 transition-colors duration-100 hover:bg-white/5 hover:text-white"
          >
            <KeyRound size={18} strokeWidth={1.75} aria-hidden="true" className="shrink-0" />
            Změnit heslo
          </button>
          <button
            type="button"
            onClick={() => void logout()}
            className="flex h-10 items-center gap-3 rounded-lg px-3 text-left text-sm font-medium text-white/70 transition-colors duration-100 hover:bg-white/5 hover:text-white"
          >
            <LogOut size={18} strokeWidth={1.75} aria-hidden="true" className="shrink-0" />
            Odhlásit se
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Horní lišta = KONTEXT: název firmy + na které zakázce pracuji. */}
        <header className="flex h-14 shrink-0 items-center gap-x-3 border-b border-border bg-surface px-3 lg:gap-x-5 lg:px-6">
          <button
            type="button"
            aria-label="Otevřít menu"
            className="flex size-10 shrink-0 items-center justify-center rounded-lg text-text-secondary hover:bg-bg hover:text-text-primary lg:hidden"
            onClick={() => setMenuOtevrene(true)}
          >
            <MenuIkona size={22} strokeWidth={2} aria-hidden="true" />
          </button>
          <div className="hidden shrink-0 truncate text-sm font-semibold text-text-heading md:block">
            {firma?.nazev ?? '…'}
          </div>
          {maPravo('projekty.read') && zakazky && zakazky.length > 0 && (
            <>
              <span aria-hidden="true" className="hidden h-5 w-px shrink-0 bg-border md:block" />
              <div className="flex min-w-0 flex-1 items-center gap-3 lg:flex-none">
                <div className="flex min-w-0 flex-1 items-center gap-2 lg:flex-none">
                  {/* Vizuální popisek jako span (ne <label>), aby getByLabelText
                      „Zakázka" nekolidoval s poli v Import wizardu — přístupný
                      název selectu drží aria-label „Vybraná zakázka". */}
                  <span className="hidden shrink-0 text-xs font-semibold tracking-wide text-text-secondary uppercase sm:block">
                    Zakázka
                  </span>
                  <Select
                    aria-label="Vybraná zakázka"
                    className="!min-h-9 w-full min-w-0 !text-sm lg:w-72"
                    value={vybranaId}
                    onChange={(e) => {
                      nastav(e.target.value);
                      const cil = cilPoPrepnutiZakazky(location.pathname);
                      if (cil && cil !== location.pathname) void navigate(cil);
                    }}
                  >
                    {zakazky.map((z) => (
                      <option key={z.id} value={z.id}>
                        {z.cislo} — {z.nazev}
                      </option>
                    ))}
                  </Select>
                </div>
                <NavLink
                  to="/zakazky"
                  className="hidden shrink-0 text-sm font-medium text-primary hover:underline xl:block"
                >
                  Všechny zakázky →
                </NavLink>
              </div>
            </>
          )}

          {/* Zvonek upozornění — vše, co čeká na reakci, na jednom místě. */}
          <div className="ml-auto">
            <Menu
              align="right"
              spoust="relative inline-flex size-10 items-center justify-center rounded-lg text-text-secondary transition-colors duration-100 hover:bg-bg hover:text-text-primary"
              stitek={
                <>
                  <Bell size={20} strokeWidth={1.75} aria-hidden="true" />
                  <span className="sr-only">Upozornění ({pocetUpozorneni})</span>
                  {pocetUpozorneni > 0 && (
                    <span
                      aria-hidden="true"
                      className={`absolute -top-0.5 -right-0.5 inline-flex min-w-4.5 items-center justify-center rounded-full px-1 text-[11px] leading-4.5 font-bold ${
                        jeAlarm ? 'bg-error text-white' : 'bg-accent text-text-heading'
                      }`}
                    >
                      {pocetUpozorneni > 9 ? '9+' : pocetUpozorneni}
                    </span>
                  )}
                </>
              }
            >
              <p className="px-2 pt-1 pb-2 text-xs font-semibold tracking-wider text-text-secondary uppercase">
                Upozornění
              </p>
              {pocetUpozorneni === 0 && (
                <p className="flex items-center gap-2 px-2 pb-2 text-sm text-text-secondary">
                  <CheckCircle2
                    size={16}
                    strokeWidth={2}
                    aria-hidden="true"
                    className="text-success-text"
                  />
                  Vše v klidu — nic nečeká na reakci.
                </p>
              )}
              <div className="flex max-h-96 w-80 max-w-full flex-col overflow-y-auto">
                {(hlidaceAktualni?.alarmy ?? []).map((h, i) => (
                  <UpozorneniRadek
                    key={`a${i}`}
                    ton="error"
                    text={h.text}
                    to={h.zmenaId ? `/zmeny/${h.zmenaId}` : '/nalezy'}
                  />
                ))}
                {(hlidaceAktualni?.pripominky ?? []).map((h, i) => (
                  <UpozorneniRadek
                    key={`p${i}`}
                    ton="warning"
                    text={h.text}
                    to={h.zmenaId ? `/zmeny/${h.zmenaId}` : '/nalezy'}
                  />
                ))}
                {pocetNovychNalezu > 0 && (
                  <UpozorneniRadek
                    ton="info"
                    text={`${pocetNovychNalezu} ${pocetNovychNalezu === 1 ? 'nový nález čeká' : pocetNovychNalezu < 5 ? 'nové nálezy čekají' : 'nových nálezů čeká'} na rozhodnutí ve frontě.`}
                    to="/nalezy"
                  />
                )}
                {nepodepsane > 0 && (
                  <UpozorneniRadek
                    ton="warning"
                    text={`${nepodepsane} ${nepodepsane === 1 ? 'nepodepsaný deník' : nepodepsane < 5 ? 'nepodepsané deníky' : 'nepodepsaných deníků'} — dopsat a podepsat.`}
                    to="/denik"
                  />
                )}
                {/* Ostatní zakázky — až po aktuální; u položky je vidět, KDE hoří. */}
                {hlidaceOstatni.length > 0 && (
                  <p className="border-t border-border px-2 pt-2 pb-1 text-xs font-semibold tracking-wider text-text-secondary uppercase">
                    Ostatní zakázky
                  </p>
                )}
                {hlidaceOstatni.flatMap((z) =>
                  [
                    ...z.alarmy.map((h, i) => ({
                      h,
                      ton: 'error' as const,
                      k: `oa${z.zakazka.id}${i}`,
                    })),
                    ...z.pripominky.map((h, i) => ({
                      h,
                      ton: 'warning' as const,
                      k: `op${z.zakazka.id}${i}`,
                    })),
                  ].map(({ h, ton, k }) => (
                    <UpozorneniRadek
                      key={k}
                      ton={ton}
                      text={`${z.zakazka.cislo}: ${h.text}`}
                      to={
                        h.zmenaId ? `/zmeny/${h.zmenaId}` : `/zakazky/${z.zakazka.id}/rekapitulace`
                      }
                    />
                  )),
                )}
              </div>
            </Menu>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto flex max-w-7xl flex-col gap-5 px-4 py-4 lg:px-8 lg:py-6">
            {/* Onboarding kanceláře: vysvětlení je na Přehledu za „?" u nadpisu. */}
            <Outlet />
          </div>
        </main>
      </div>
      {menimHeslo && <ZmenaHeslaDialog onZavrit={() => setMenimHeslo(false)} />}
    </div>
  );
}
